import { ReferenceFields } from 'honeygrid-types'

export const avatarLabel = (name?: string) => {
	if (!name) {
		return ''
	}

	const [first, second] = name.split(' ')
	if (second) {
		return `${first.at(0)}${second.at(0)}`.toUpperCase()
	}

	return `${first.slice(0, 2)}`.toUpperCase()
}

/***
 * takes in a number and returns a properly formatted string
 * with a currency in the beginning and commas
 */

export const formatCurrency = (amount?: number): string => {
	// Check if the input is a valid number
	if (typeof amount !== 'number' || isNaN(amount)) {
		return 'Invalid Input'
	}

	// Convert the number to a formatted string with commas and a dollar sign

	const formattedCurrency = amount.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		// remove decimals
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	})

	return formattedCurrency
}

/** Takes in a hex color and returns a rgba color with the specified alpha ready to use with css. */
export const hexToRGBA = (hex: string, alpha: number): string => {
	// check that alpha is positive
	if (alpha < 0) {
		alpha = 0
	}
	const cleanedHex = hex.replace('#', '')

	const red = parseInt(cleanedHex.slice(0, 2), 16)
	const green = parseInt(cleanedHex.slice(2, 4), 16)
	const blue = parseInt(cleanedHex.slice(4, 6), 16)

	const rgba = `rgba(${red}, ${green}, ${blue}, ${alpha})`

	return rgba
}

// A utility function to format stat value based on displayMode
export const formatStat = (displayMode: string, value: any) => {
	if (displayMode === 'percentage') {
		return `${value}%`
	}
	return value
}

// Utility functions for formatting legend values
/**
 *
 * @param value to be formatted.
 * @param selectedReferenceField to determine how to format the value.
 * @returns formatted legend value based on selectedReferenceField.
 */
export const formatValueLegend = (value: number, selectedReferenceField: string) => {
	switch (selectedReferenceField) {
		case ReferenceFields.CustomerCount:
			return legendFormatNumeric(value)
		case ReferenceFields.CustomerSales:
			return legendFormatNumeric(value)
		case ReferenceFields.CustomerMarketShare:
			return legendFormatPercentage(value)
		case ReferenceFields.Households:
			return legendFormatNumeric(value)
		case ReferenceFields.HomeValues:
			return legendFormatCurrency(value)
		case ReferenceFields.Income:
			return legendFormatCurrency(value)
		case ReferenceFields.NewMoverTotal:
			return legendFormatNumeric(value)
		case ReferenceFields.NewMoverSFDU:
			return legendFormatNumeric(value)
		case ReferenceFields.NewMoverMFDU:
			return legendFormatNumeric(value)
		case ReferenceFields.SFDU:
			return legendFormatNumeric(value)
		case ReferenceFields.MFDU:
			return legendFormatNumeric(value)
		default:
			return `${value}`
	}
}

export const legendFormatCurrency = (value: number) => {
	if (value >= 1000000) {
		const formattedValue = Math.floor(value / 1000000)
		return `$${formattedValue}m`
	} else if (value >= 1000) {
		const formattedValue = Math.floor(value / 1000)
		return `$${formattedValue}k`
	} else {
		return `$${Math.floor(value)}`
	}
}

export const legendFormatPercentage = (value: number) => {
	const formattedValue = (value * 100).toFixed(1)
	return `${formattedValue}%`
}

export const legendFormatNumeric = (value: number) => {
	if (value >= 1000000) {
		const formattedValue = Math.floor(value / 1000000)
		return `${formattedValue}m`
	} else if (value >= 1000) {
		const formattedValue = Math.floor(value / 1000)
		return `${formattedValue}k`
	} else {
		return `${value}`
	}
}
